import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { useClipQueue } from '../../lib/clipQueue'
import { MAX_SECONDS, MIN_SECONDS, makeStillClip, canEncode, mediaSeconds } from '../../lib/videoClip'
import { Shell } from '../../components/Shell'
import TrimStrip from '../../components/TrimStrip'
import { Button, Field, Sill } from '../../components/ui'

/**
 * Add a Collective clip — the short way in.
 *
 * The song wizard exists for a finished song: declarations, splits, a chart, a
 * church. That is the right amount of ceremony for something a congregation
 * will sing, and far too much for a chorus somebody wants other writers to
 * hear. Nothing was reaching the feed because the only route to it ran through
 * all of that.
 *
 * So this makes the clip the primitive and lets the song grow from it. What it
 * creates is a real but deliberately unfinished song: a title, Collective
 * reach, and a clip. No declarations, no submission, no reviewer inbox — an
 * idea is not a submission, and letting one into the church flow would put
 * fragments in front of a worship pastor and leave the rights model standing
 * on nothing. Finishing it properly is a separate, later step in the wizard.
 *
 * Two ways in, because most writers will never film themselves:
 *   - a video, which is the best version of this; or
 *   - a photograph and a recording, muxed into the same kind of clip.
 *
 * Both are previewed in the real feed chrome before anything is uploaded. What
 * you see here is what the slide looks like, because it is the same markup.
 */
export default function CollectiveClip() {
  const { user, profile } = useSession()
  const nav = useNavigate()
  const { job, start } = useClipQueue()

  const [title, setTitle] = useState('')
  const [video, setVideo] = useState(null)   // { file, url, seconds }
  const [image, setImage] = useState(null)   // { file, url }
  const [audio, setAudio] = useState(null)   // { file, url, seconds }
  // Which part of the picked video becomes the clip. Set the moment a file
  // lands, so posting without touching the strip behaves exactly as it always
  // did -- from the top, capped.
  const [win, setWin] = useState({ start: 0, end: MAX_SECONDS })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const videoIn = useRef(null)
  const imageIn = useRef(null)
  const audioIn = useRef(null)

  // Object URLs are a leak if nobody revokes them, and this screen makes up to
  // three of them per visit while someone changes their mind.
  useEffect(() => () => {
    for (const u of [video?.url, image?.url, audio?.url]) if (u) URL.revokeObjectURL(u)
  }, [video, image, audio])

  const tooShort = (secs) =>
    `That's ${Math.round(secs)} seconds. A Collective clip needs at least `
    + `${MIN_SECONDS} — long enough for someone to hear the idea. `
    + `Anything past ${MAX_SECONDS} is trimmed.`

  const pickVideo = async (file) => {
    setErr(null)
    const secs = await mediaSeconds(file, 'video')
    if (!Number.isFinite(secs)) return setErr("That video couldn't be read.")
    if (secs < MIN_SECONDS) return setErr(tooShort(secs))
    setImage(null); setAudio(null)
    setWin({ start: 0, end: Math.min(secs, MAX_SECONDS) })
    setVideo({ file, url: URL.createObjectURL(file), seconds: secs })
  }

  const pickAudio = async (file) => {
    setErr(null)
    const secs = await mediaSeconds(file, 'audio')
    if (!Number.isFinite(secs)) return setErr("That recording couldn't be read.")
    if (secs < MIN_SECONDS) return setErr(tooShort(secs))
    setVideo(null)
    setAudio({ file, url: URL.createObjectURL(file), seconds: secs })
  }

  const pickImage = (file) => {
    setErr(null)
    setVideo(null)
    setImage({ file, url: URL.createObjectURL(file) })
  }

  const ready = !!video || (!!image && !!audio)
  const seconds = video
    ? win.end - win.start
    : Math.min(audio?.seconds ?? 0, MAX_SECONDS)

  /**
   * Make the song, then hand the clip to the queue and leave.
   *
   * The queue lives above the router precisely so the encode and upload survive
   * this screen unmounting — so the writer is sent to the Collective the moment
   * the work is handed over, not made to watch a bar.
   *
   * A clip posts outright: it is not a draft waiting on a co-sign. One person
   * uploading their own hook to the Collective owns it by definition, and
   * asking them to counter-sign themselves before anyone could see it left
   * every clip stranded in Drafts. The signature gate exists to stop one
   * writer settling ownership of something other people wrote — nothing here
   * claims anyone else's work, so it has nothing to hold. It still applies in
   * full the moment this becomes a real song with real co-writers.
   */
  const post = async () => {
    setBusy(true); setErr(null)
    try {
      const now = new Date().toISOString()
      const { data: song, error } = await supabase.from('songs').insert({
        songwriter_id: user.id,
        title: title.trim() || 'Untitled',
        // Published to the Collective, and finished_at so it stops reading as
        // a draft. Not submitted_at — no church has this, and overloading that
        // column would make posting a clip block ever sending the song.
        status: 'community_published',
        finished_at: now,
        reach: 'collective',
        reach_set_at: now,
        // Twenty-four hours, unless it becomes a real song first. An idea in
        // the feed that nobody -- including the writer -- came back to should
        // not still be in their catalogue next year. Writing any lyrics,
        // confirming the declarations or sending it clears this; when it does
        // run out the song goes to Recently deleted for thirty days, not to
        // nowhere.
        clip_expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      }).select().single()
      if (error) throw error

      // The writer is a co-writer of their own song, signed on the spot: their
      // own upload is their own claim. Splits stay the finishing step's
      // problem, but the primary row has to exist from the start or the song
      // has no owner in the rights model at all.
      const me = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || user.email
      await supabase.from('song_cowriters').insert({
        song_id: song.id, cowriter_name: me, cowriter_email: user.email,
        user_id: user.id, split_percentage: 100, is_primary: true,
        signed: true, signed_at: now, signature_name: me,
      })

      if (video) {
        start(video.file, song, null, 'original', null, win)
      } else {
        // Encoded here rather than in the queue: the preview above is this same
        // composition, so by the time someone presses post the decision about
        // what it looks like has already been made.
        const prepared = await makeStillClip(image.file, audio.file, {})
        start(prepared.file, song, null, 'original', prepared)
      }
      nav('/collective')
    } catch (e) {
      setBusy(false)
      setErr(readableError(e))
    }
  }

  const working = busy || job?.phase === 'preparing' || job?.phase === 'posting'

  return (
    <Shell tabs back="/create" title="Create" hidePlayer>
      <div className="eyebrow">The collective</div>
      <h2 className="q">Put an idea in the feed.</h2>
      <p className="sub">
        It doesn't have to be finished — a chorus, a hook, the bit that works.
        Other writers scroll this, and nothing here goes to a church.
      </p>
      <p className="small" style={{ marginTop: 8 }}>
        A clip stays up for 24 hours. Start writing it into a full song and it
        keeps — otherwise it goes to Recently deleted, where you have thirty
        days to bring it back.
      </p>
      <Sill />

      <Field label="What's it called?">
        <input value={title} maxLength={80} placeholder="Untitled"
               onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <p className="small" style={{ marginTop: 14 }}>
        Between {MIN_SECONDS} and {MAX_SECONDS} seconds. Vertical video is best
        — it fills the screen.
      </p>

      <input ref={videoIn} type="file" hidden accept="video/*,.mov,.mp4,.m4v,.webm"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pickVideo(f) }} />
      <input ref={imageIn} type="file" hidden accept="image/*"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pickImage(f) }} />
      <input ref={audioIn} type="file" hidden accept="audio/*,.m4a,.mp3,.wav,.aac"
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pickAudio(f) }} />

      <button type="button" className="btn btn-primary" style={{ marginTop: 6 }}
              disabled={working} onClick={() => videoIn.current?.click()}>
        {video ? 'Choose a different video' : 'Choose a video'}
      </button>

      <p className="small center" style={{ margin: '14px 0 10px', opacity: .7 }}>
        or, if you haven't filmed anything
      </p>

      <div className="row" style={{ display: 'flex', gap: 10 }}>
        <button type="button" className="btn btn-quiet" style={{ flex: 1 }}
                disabled={working} onClick={() => imageIn.current?.click()}>
          {image ? 'Change picture' : 'Add a picture'}
        </button>
        <button type="button" className="btn btn-quiet" style={{ flex: 1 }}
                disabled={working} onClick={() => audioIn.current?.click()}>
          {audio ? 'Change recording' : 'Add a recording'}
        </button>
      </div>

      {image && !audio && (
        <p className="small" style={{ marginTop: 10 }}>
          Now add the recording — the picture is the background, the recording is
          the song.
        </p>
      )}
      {audio && !image && !video && (
        <p className="small" style={{ marginTop: 10 }}>
          Now add a picture to sit behind it.
        </p>
      )}

      {/* Trim before preview, because trimming is the decision and the
          preview is the check on it. The hook is almost never at zero -- a
          phone video opens on someone walking back to the guitar -- and
          without this the encoder silently kept that and dropped the chorus. */}
      {video && (
        <>
          <h3 className="q" style={{ fontSize: 17, marginTop: 26 }}>Which bit?</h3>
          <p className="small" style={{ marginBottom: 0 }}>
            Drag the ends to pick the part worth hearing. Drag the middle to
            move it without changing how long it is.
          </p>
          <TrimStrip url={video.url} seconds={video.seconds}
                     value={win} onChange={setWin} />
        </>
      )}

      {ready && (
        <>
          <h3 className="q" style={{ fontSize: 17, marginTop: 26 }}>How it will look</h3>
          <p className="small" style={{ marginBottom: 12 }}>
            This is the slide, not an impression of it.
          </p>
          <ClipPreview title={title} video={video} image={image} audio={audio}
                       seconds={seconds} from={video ? win.start : 0} writer={
                         [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'You'
                       } />
        </>
      )}

      {!canEncode() && ready && (
        <p className="small" style={{ marginTop: 12 }}>
          This browser can't shrink video, so the file goes up as it is. It will
          still play — it just takes longer to send.
        </p>
      )}

      {err && <p className="err" style={{ marginTop: 12 }}>{err}</p>}

      <Button onClick={post} busy={working} disabled={!ready}
              style={{ marginTop: 16, width: '100%' }}>
        Post to the Collective
      </Button>
      <p className="small" style={{ marginTop: 10 }}>
        You can carry on using the app while it uploads.
      </p>
    </Shell>
  )
}

/**
 * The feed slide, rendered locally against files that have not been uploaded.
 *
 * Deliberately the same class names as the real thing rather than a mockup of
 * it: a preview that drifts from the feed is worse than no preview, because it
 * is confidently wrong about the one question it exists to answer.
 */
function ClipPreview({ title, video, image, audio, seconds, from = 0, writer }) {
  const media = useRef(null)
  const [pct, setPct] = useState(0)

  // The preview plays the trimmed window, not the file. `from` is zero for
  // the picture-plus-recording path, which is muxed from the start already.
  const onTime = (e) => {
    const span = Math.min(seconds || MAX_SECONDS, MAX_SECONDS)
    const t = e.target.currentTime - from
    if (t >= span || t < -0.25) { e.target.currentTime = from; return }
    setPct((t / span) * 100)
  }

  return (
    <div className="rl-preview">
      <div className="rl-slide">
        {video
          ? <video ref={media} src={video.url} playsInline muted controls
                   onTimeUpdate={onTime}
                   onLoadedMetadata={(e) => { e.target.currentTime = from }} />
          : <>
              <img src={image.url} alt="" className="rl-still" />
              <audio ref={media} src={audio.url} loop controls
                     onTimeUpdate={onTime} className="rl-prevaudio" />
            </>}

        <div className="rl-head">
          <span className="rl-byline">{writer}</span>
          <span className="rl-title">{title.trim() || 'Untitled'}</span>
        </div>

        {/* The rail and the reach pip are shown but inert: this is what the
            slide looks like, not a place to use it. */}
        <div className="rl-rail" aria-hidden="true">
          <span className="rl-ico">♡</span>
          <span className="rl-ico">i</span>
        </div>

        <div className="rl-over">
          <span className="rl-pip">The Collective</span>
          <span className="rl-cta" role="presentation">Hear the whole song</span>
        </div>

        <div className="rl-prog"><i style={{ width: pct + '%' }} /></div>
      </div>
    </div>
  )
}
